import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Tooltip, Skeleton, Dropdown, Button, Menu, notification } from "antd"
import { DownloadOutlined, PlusOutlined, UploadOutlined, EditOutlined, SearchOutlined, FilterOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons"

import { fetchLedgerAccounts, selectLedgerAccounts, selectTotal, selectPagination, selectLoading, filterLedgerAccounts, deleteLedgerAccount, generateCsv } from "../ledgerAccountsSlice"
import { allObjectValuesEmpty } from "../../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnSearchFilter from "../../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnToggleFilter from "../../../../components/utilityComponents/table/ColumnToggleFilter"
import ColumnHeaderTitle from "../../../../components/utilityComponents/table/ColumnHeaderTitle"
import { tableFiltersApplied } from "../../../../functions/objectFunctions/tableFiltersApplied"
import useConfirmationModal from "../../../alertsPanel/ShowConfirm"

import Role from "../../../userRoles/Role"

export default function LedgerAccountTable({ scopedByClient }) {
    /////*** react router start ***/////
    const { url } = useRouteMatch()
    let { clientId } = useParams()
    /////*** react router end ***/////

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const ledgerAccounts = useSelector(selectLedgerAccounts)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)
    const loading = useSelector(selectLoading)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    const [filters, setFilters] = useState({})
    const [sort, setSort] = useState({
        column: "name",
        direction: "asc",
    })
    /////*** useState end ***/////

    /////*** useEffects start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialLedgerAccounts()
        }
    }, [loaded, url, dispatch, pagination]) // eslint-disable-line
    /////*** useEffects end ***/////

    /////*** event handler methods start ***/////
    const handleFetchInitialLedgerAccounts = () => {
        dispatch(fetchLedgerAccounts(clientId, pagination))
    }

    const handleFilterLedgerAccounts = (newPagination = null, newSort = null) => {
        return dispatch(
            filterLedgerAccounts({
                clientId: clientId,
                filters: filters,
                pagination: !!newPagination ? newPagination : pagination,
                sort: !!newSort ? newSort : sort,
            })
        )
    }

    const showConfirm = useConfirmationModal();
    const handleDeleteClick = (id, clientId) => {
        showConfirm(deleteLedgerAccount, id, clientId);
    };

    const handleSortLedgerAccountsChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        setSort(newSort)
        handleFilterLedgerAccounts(null, newSort)
    }

    const handleTableChange = (newPagination, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            dispatch(fetchLedgerAccounts(clientId, newPagination))
        } else {
            handleFilterLedgerAccounts(newPagination)
        }
    }
    /////*** event handler methods end ***/////

    const renderColumnHeaderTitle = (title) => {
        return <ColumnHeaderTitle title={title} handleSortChange={handleSortLedgerAccountsChange} sort={sort} />
    }

    const accountsCsv = () => {
        notification.open({
            message: "Ledger Account's export started",
            duration: 0.8,
        })

        dispatch(generateCsv(clientId, filters))
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters)) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="javascript:;"
                    onClick={() => {
                        handleFetchInitialLedgerAccounts()
                        setFilters({})
                        setSort({ column: "name", direction: "asc" })
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : "#9a9a9b" }} />
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Ledger Accounts</h2>
                            <div className="badge">{total}</div>
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {renderResetFilters()}

                                <Role action="create" model="ledger_account">
                                    <Link to={`${url}/new`}>
                                        <PlusOutlined />
                                        Add New
                                    </Link>
                                </Role>

                                <Link to={`${url}/import`}>
                                    <PlusOutlined />
                                    Import
                                </Link>

                                {/* {scopedByClient && ( */}
                                    <Role action="update" model="bulk_edit">
                                        {/* <Link to={scopedByClient ? `/clients/${scopedByClient}/billable_accounts/bulk_edit` : `/billable_accounts/bulk_edit`}> */}
                                        <Link to={`/clients/${clientId}/ledger_accounts/bulk_edit`}>
                                            <EditOutlined />
                                            Bulk Edit
                                        </Link>
                                    </Role>
                                {/* )} */}

                                <a href="javascript:;" onClick={() => accountsCsv()}>
                                    <DownloadOutlined />
                                    Export
                                </a>
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading}>
                        <Table dataSource={ledgerAccounts} pagination={{ ...pagination, showSizeChanger: true, total: total }} onChange={handleTableChange} rowKey={(r) => r.id}>
                            <Table.Column
                                title={renderColumnHeaderTitle("name")}
                                key="name"
                                dataIndex="name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialLedgerAccounts}
                                        filterAction={filterLedgerAccounts}
                                        filterColumn={"name"}
                                        filterType={"name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("active")}
                                key="active"
                                dataIndex="active"
                                render={(text, record) => <>{record.active ? "Active" : "Inactive"}</>}
                                filterDropdown={({ visible }) => (
                                    <ColumnToggleFilter
                                        handleFetchInitial={handleFetchInitialLedgerAccounts}
                                        filterAction={filterLedgerAccounts}
                                        filterColumn={"active"}
                                        filterType={"active_eq"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("active_eq")}
                            />
                            <Table.Column
                                title="Edit"
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    <Role action="update" model="ledger_account">
                                                        <Menu.Item key="1" icon={<EditOutlined />}>
                                                            <Link to={`${url}/${record.id}/edit`}>Edit Ledger Account</Link>
                                                        </Menu.Item>
                                                        <Menu.Item key="2" icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record.id, clientId)}>
                                                            Delete Ledger Account
                                                        </Menu.Item>
                                                    </Role>
                                                    {scopedByClient && (
                                                        <Menu.Item key="3">
                                                            <Link to={`${url}/${record.id}/dashboard`}>View</Link>
                                                        </Menu.Item>
                                                    )}
                                                </Menu>
                                            }>
                                            <Button>
                                                Actions <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
            {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
        </div>
    )
}
